import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowRight, Banknote, FileText, Lock, Unlock } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { Booking } from '../data/model'
import { nights, todayISO } from '../lib/dates'
import { Badge, Button, Sheet, cx } from '../ui/kit'

export interface PendingChange {
  booking: Booking
  unitId: string
  arrival: string
  departure: string
  /** Der ganze Balken ist gewandert — oder nur eines seiner beiden Enden. */
  kind: 'move' | 'resize'
}

/**
 * Confirmation for a dragged booking.
 *
 * A move is not just new dates — it usually changes the price, and if a confirmation
 * has already gone to the guest it also makes that document wrong. Both consequences
 * are shown here before anything is written, because the landlord is the only one who
 * knows whether the agreed price still stands.
 *
 * ════════════════════════════════════════════════════════════════════════════════
 *  WARUM EIN BLATT FÜR BEIDE GESTEN
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * Verschieben und Längenändern stellen Zeile für Zeile dieselben Fragen: Preis,
 * Kollision, Portalereignis, veraltete Bestätigung, Rechnung, Vergangenheit. Dass
 * dieses Blatt Rechnungen bis vor kurzem GAR NICHT ansah, ist der Beweis, wohin
 * zwei getrennte Blätter führen: eine Frage wird an einer Stelle beantwortet und an
 * der anderen vergessen. Am `kind` hängen deshalb nur Titel und wenige Sätze.
 */
export default function BookingChangeDialog({
  change, onClose,
}: { change: PendingChange | null; onClose: () => void }) {
  const { t, lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const navigate = useNavigate()
  const de = lang === 'de'

  // `null` heißt „noch nicht angefasst" — dann entscheidet der Vorschlag unten.
  const [reQuote, setReQuote] = useState<boolean | null>(null)

  const moved: Booking | null = change
    ? {
        ...change.booking,
        unit_id: change.unitId, arrival: change.arrival, departure: change.departure,
      }
    : null

  const oldPriced = useMemo(
    () => (change ? store.price(change.booking) : null), [change, store])
  const newPriced = useMemo(
    () => (moved ? store.price(moved) : null), [moved, store])

  if (!change || !moved) return null

  const isResize = change.kind === 'resize'
  /*
   * Eine Sperre hat weder Gast noch Preis. Sie trotzdem durch die Preisrechnung zu
   * schicken ergibt eine Zahl — der Handwerkertermin bekäme ein Preisschild und ein
   * Angebotshäkchen. Preis- und Angebotsteil entfallen für sie deshalb ganz; das
   * Blatt zeigt dann nur die Daten.
   */
  const isBlock = change.booking.kind === 'block'

  const oldTotal = change.booking.price_locked
    ? change.booking.total_gross
    : (oldPriced?.totals.gross ?? change.booking.total_gross)
  const newTotal = newPriced?.totals.gross ?? oldTotal
  const delta = Math.round((newTotal - oldTotal) * 100) / 100
  const priceChanges = !isBlock && Math.abs(delta) >= 0.01

  const blocked = !store.isAvailable(
    change.unitId, change.arrival, change.departure, change.booking.id)

  /*
   * Was eine ausgestellte Rechnung mit dem Verschieben zu tun hat.
   *
   * Bis hierher sah dieses Blatt Rechnungen GAR NICHT an — es warnte nur vor der
   * veralteten Bestätigung. Eine Buchung mit ausgestellter Rechnung ließ sich damit
   * verschieben, und mit „Neuer Preis" nannte die Rechnung danach einen anderen
   * Betrag und andere Nächte als die Buchung. Das ist der Fehler, den niemand im
   * Betrieb bemerkt: er kommt beim Jahresabschluss wieder hoch oder beim Gast.
   *
   * Der Entwurf sperrt nicht — er wird genannt und stehen gelassen. Ihn beim
   * Verschieben mitzuschreiben wäre eine automatische Rechnungskorrektur, und die
   * ist ausdrücklich nicht gewollt.
   */
  const locking = store.invoiceLockFor(change.booking.id)
  const drafts = store.draftInvoicesFor(change.booking.id)
  const locked = locking.length > 0

  const confirmationIsStale =
    !!change.booking.confirmation_sent_at || change.booking.status === 'confirmed'

  const fromUnit = store.unitById(change.booking.unit_id)
  const toUnit = store.unitById(change.unitId)

  /** Am Anreisetag hängt mehr als ein Datum — deshalb eigens gemerkt. */
  const arrivalMoved = change.arrival !== change.booking.arrival

  /** Eine angenommene Zusage ist eine Vereinbarung, nicht bloß ein verschicktes PDF. */
  const acceptedQuote = store.db.quotes.some(
    (q) => q.booking_id === change.booking.id && q.status === 'accepted')

  /**
   * Ob dem Gast ein neues Angebot geschickt werden soll.
   *
   * Vorgeschlagen wird es, wenn der Preis STEIGT und der Gast schon eine Bestätigung
   * hat: dann ist die Verschiebung kein interner Vorgang mehr, sondern eine Änderung
   * des Vereinbarten, und die muss der Gast bestätigen. Bei einem niedrigeren Preis
   * ist es nicht nötig — freuen darf sich der Gast auch ohne Unterschrift.
   *
   * Die ANREISE ist der zweite Anlass, und sie war lange übersehen: mit ihr wandert
   * das Stornofenster, in dem der Gast steht. Die Fristen stehen als Text im Angebot
   * und werden bei der Annahme eingefroren — sie rechnen sich nicht von selbst nach.
   * Eine um zwei Tage vorgezogene Anreise ändert das Vereinbarte also auch dann,
   * wenn die Summe auf den Cent gleich bleibt.
   */
  const suggestReQuote = !isBlock && (
    (priceChanges && delta > 0 && confirmationIsStale)
    || (arrivalMoved && (confirmationIsStale || acceptedQuote)))
  const wantsReQuote = reQuote ?? suggestReQuote
  const showReQuote = !isBlock && (priceChanges || confirmationIsStale || suggestReQuote)

  /*
   * Die Anzahlung wird NICHT nachgerechnet — aber sie wird genannt.
   *
   * `saveBooking` behält einen bereits gesetzten Betrag bei (`b.prepayment_amount ||
   * …`), und das ist richtig so: der Vermieter, der mit einem Gast 50 € statt 30 %
   * vereinbart hat, verlöre das sonst beim nächsten beliebigen Speichern. Die Folge
   * ist trotzdem eine zu niedrige Forderung, und die gehört dem Vermieter vor Augen
   * und nicht in ein Protokoll.
   */
  const prepaymentStale = !isBlock && priceChanges && change.booking.prepayment_amount > 0
  /**
   * Die Anzahlungsfrist ist „heute + 7" und hängt nicht an der Anreise. Wandert die
   * Anreise davor, ist die Anzahlung erst nach der Ankunft fällig — gerechnet wird
   * hier nichts, gesagt schon.
   */
  const prepaymentDue = change.booking.prepayment_due ?? null
  const prepaymentAfterArrival = arrivalMoved
    && !!prepaymentDue
    && change.booking.prepayment_amount > 0
    && change.arrival < prepaymentDue

  const apply = async (keepPrice: boolean) => {
    const next: Booking = {
      ...moved,
      // Für eine Sperre gibt es nichts festzuhalten: sie wird ohnehin nicht
      // bepreist, und ein Festpreisvermerk auf einem Handwerkertermin ist Unsinn.
      ...(isBlock ? {} : {
        price_locked: keepPrice,
        price_locked_note: keepPrice
          ? (de
            ? (isResize
              ? 'Preis bei Änderung des Zeitraums beibehalten'
              : 'Preis bei Verschiebung beibehalten')
            : (isResize
              ? 'Price kept when the dates were changed'
              : 'Price kept when the booking was moved'))
          : null,
        // A locked price keeps the stored totals; otherwise saveBooking re-prices.
        ...(keepPrice ? {} : { total_gross: newTotal }),
      }),
      moved_at: new Date().toISOString(),
    }
    // `saveBooking` weist Buchungen ohne Gast ab (Namenszwang) und meldet das über
    // den Fehlerstreifen. Das Blatt darf dann NICHT schließen und erst recht kein
    // Angebot erzeugen — sonst verschwindet die Änderung scheinbar erfolgreich,
    // und der Vermieter findet die Buchung am alten Platz wieder.
    const saved = await store.saveBooking(next)
    if (!saved) return

    // Nur mit dem neuen Preis. Bei „Preis behalten" bleibt der vereinbarte Betrag
    // stehen, das Angebot würde aber aus den Saisonpreisen neu rechnen und damit eine
    // Zahl nennen, die so nie vereinbart war. Lieber kein Dokument als ein falsches.
    if (wantsReQuote && !keepPrice) {
      // Mit der geänderten Buchung als Vorlage: der Store hat den neuen Stand in
      // dieser Runde noch nicht, und ein Angebot mit den alten Daten wäre schlimmer
      // als gar keines.
      const id = await store.createQuote(next.id, next)
      onClose()
      if (id) navigate(`/document/quote/${id}`)
      return
    }
    onClose()
  }

  return (
    <Sheet
      open={!!change}
      onClose={onClose}
      title={isResize
        ? (de ? 'Zeitraum ändern' : 'Change the dates')
        : (de ? 'Buchung verschieben' : 'Move booking')}
      footer={
        <>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {priceChanges && (
            <Button variant="secondary" className="flex-1" disabled={blocked || locked}
              onClick={() => apply(true)}>
              <Lock size={15} /> {de ? 'Preis behalten' : 'Keep price'}
            </Button>
          )}
          <Button variant="primary" className="flex-1" disabled={blocked || locked}
            onClick={() => apply(false)}>
            {priceChanges
              ? <><Unlock size={15} /> {de ? 'Neuer Preis' : 'New price'}</>
              : isResize
                ? t('common.save')
                : (de ? 'Verschieben' : 'Move')}
          </Button>
        </>
      }
    >
      {/* Die härteste Sperre steht zuoberst — und sie nennt den Ausweg mit. Eine
          Sperre ohne genannten Ausweg wird umgangen, nicht befolgt. */}
      {locked && (
        <div className="rounded-xl px-3 py-2.5 text-sm bg-danger-soft text-danger space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">
                {de
                  ? (isResize
                    ? 'Diese Buchung ist abgerechnet — Anreise und Abreise sind festgeschrieben.'
                    : 'Diese Buchung ist abgerechnet und lässt sich nicht verschieben.')
                  : (isResize
                    ? 'This booking has been invoiced — arrival and departure are fixed.'
                    : 'This booking has been invoiced and cannot be moved.')}
              </div>
              <p className="text-xs mt-1">
                {de
                  ? 'Wenn sich der Aufenthalt wirklich geändert hat: Rechnung stornieren, Buchung ändern, neu ausstellen.'
                  : 'If the stay really did change: cancel the invoice, change the booking, issue a new one.'}
              </p>
            </div>
          </div>
          <ul className="space-y-1">
            {locking.map((i) => (
              <li key={i.id}>
                <button
                  className="text-left text-xs underline underline-offset-2"
                  onClick={() => { const id = i.id; onClose(); navigate(`/document/invoice/${id}`) }}
                >
                  {i.number ?? '—'} · {formatDate(i.issue_date)} · {formatMoney(i.total_gross)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {blocked && (
        <div className="flex items-start gap-2 rounded-xl px-3 py-2 text-sm bg-danger-soft text-danger">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>{t('booking.overlaps')}</span>
        </div>
      )}

      {/* Der Entwurf hält nicht auf, aber er weiß nach der Änderung noch die
          alten Nächte. Also: benennen, hinführen, in Ruhe lassen. */}
      {!locked && drafts.length > 0 && (
        <div className="rounded-xl px-3 py-2.5 text-sm bg-warn-soft text-warn space-y-1.5">
          <div className="flex items-start gap-2">
            <FileText size={16} className="shrink-0 mt-0.5" />
            <span>
              {de
                ? (drafts.length > 1
                  ? 'Rechnungsentwürfe zu dieser Buchung enthalten weiterhin die alten Nächte. Sie werden nicht mitgeschrieben.'
                  : 'Ein Rechnungsentwurf zu dieser Buchung enthält weiterhin die alten Nächte. Er wird nicht mitgeschrieben.')
                : (drafts.length > 1
                  ? 'Draft invoices for this booking still hold the old nights. They are not updated along with this change.'
                  : 'A draft invoice for this booking still holds the old nights. It is not updated along with this change.')}
            </span>
          </div>
          <ul className="space-y-1">
            {drafts.map((i) => (
              <li key={i.id}>
                <button
                  className="text-left text-xs underline underline-offset-2"
                  onClick={() => { const id = i.id; onClose(); navigate(`/document/invoice/${id}`) }}
                >
                  {de ? 'Entwurf' : 'Draft'} · {formatDate(i.issue_date)} · {formatMoney(i.total_gross)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-surface-2 rounded-2xl p-3 space-y-2 text-sm">
        <Row
          label={t('booking.unit')}
          from={fromUnit?.name ?? '—'}
          to={toUnit?.name ?? '—'}
        />
        <Row
          label={t('booking.arrival')}
          from={formatDate(change.booking.arrival)}
          to={formatDate(change.arrival)}
        />
        <Row
          label={t('booking.departure')}
          from={formatDate(change.booking.departure)}
          to={formatDate(change.departure)}
        />
        <Row
          label={t('common.nights')}
          from={String(nights(change.booking.arrival, change.booking.departure))}
          to={String(nights(change.arrival, change.departure))}
        />
      </div>

      {!isBlock && (priceChanges ? (
        <div className="rounded-2xl p-3 bg-warn-soft text-warn space-y-1">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle size={16} />
            {de ? 'Der Preis ändert sich' : 'The price changes'}
          </div>
          <div className="flex items-baseline gap-2 text-sm">
            <span className="line-through opacity-70">{formatMoney(oldTotal)}</span>
            <ArrowRight size={14} />
            <span className="font-semibold text-base">{formatMoney(newTotal)}</span>
            <span className="ml-auto tabular-nums">
              {delta > 0 ? '+' : ''}{formatMoney(delta)}
            </span>
          </div>
          <p className="text-xs">
            {de
              ? '„Preis behalten" schreibt den vereinbarten Betrag fest — die Saisonpreise ändern ihn dann nicht mehr.'
              : '"Keep price" fixes the agreed amount — seasonal rates will no longer change it.'}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted">
          {de ? 'Der Preis bleibt gleich.' : 'The price stays the same.'}
        </p>
      ))}

      {/* Zwei Zeilen über die Anzahlung, beide nur benennend: hier wird bewusst
          nichts nachgerechnet. Welcher Anzahlungsbetrag nach einer Preisänderung
          gewinnt, ist eine fachliche Frage, die auf JEDEM Speicherweg gilt — sie
          gehört nicht in eine Geste des Kalenders. */}
      {prepaymentStale && (
        <p className="flex items-start gap-2 text-xs text-muted">
          <Banknote size={14} className="shrink-0 mt-0.5" />
          <span>
            {de
              ? `Anzahlung ${formatMoney(change.booking.prepayment_amount)} von ${formatMoney(newTotal)} — bleibt unverändert.`
              : `Prepayment ${formatMoney(change.booking.prepayment_amount)} of ${formatMoney(newTotal)} — stays as it is.`}
          </span>
        </p>
      )}

      {prepaymentAfterArrival && prepaymentDue && (
        <p className="flex items-start gap-2 text-xs text-muted">
          <Banknote size={14} className="shrink-0 mt-0.5" />
          <span>
            {de
              ? `Die Anzahlung ist am ${formatDate(prepaymentDue)} fällig — nach der neuen Anreise am ${formatDate(change.arrival)}.`
              : `The prepayment is due on ${formatDate(prepaymentDue)} — after the new arrival on ${formatDate(change.arrival)}.`}
          </span>
        </p>
      )}

      {/* Der Weg zurück zum Gast. Ohne ihn endet die Änderung mit einer
          Buchung, die der Vermieter kennt und der Gast nicht. */}
      {showReQuote && (
        <label className="flex items-start gap-2.5 rounded-2xl px-3 py-2.5 bg-surface-2
                          text-sm cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={wantsReQuote}
            onChange={(e) => setReQuote(e.target.checked)}
          />
          <span>
            <span className="font-medium flex items-center gap-1.5">
              <FileText size={14} />
              {de ? 'Neues Angebot erstellen' : 'Create a new quote'}
            </span>
            <span className="text-xs text-muted block mt-0.5">
              {de
                ? 'Mit den neuen Daten und dem neuen Preis. Wird nach dem Speichern geöffnet, damit du es dem Gast zur Bestätigung schicken kannst.'
                : 'With the new dates and the new price. It opens once saved so you can send it to the guest for confirmation.'}
            </span>
            {arrivalMoved && !priceChanges && (
              <span className="text-xs text-muted block mt-0.5">
                {de
                  ? 'Der Preis bleibt gleich — die Anreise verschiebt sich aber, und mit ihr die Stornofrist aus dem Angebot.'
                  : 'The price is unchanged — but the arrival moves, and with it the cancellation deadline from the quote.'}
              </span>
            )}
            {priceChanges && (
              <span className="text-xs text-muted block mt-0.5">
                {de
                  ? 'Gilt für „Neuer Preis" — wer den Preis behält, hat nichts Neues anzubieten.'
                  : 'Applies to "New price" — keeping the price leaves nothing new to offer.'}
              </span>
            )}
          </span>
        </label>
      )}

      {!isBlock && change.booking.price_locked && (
        <p className="text-xs text-muted flex items-center gap-1.5">
          <Lock size={13} />
          {de
            ? 'Für diese Buchung ist bereits ein Festpreis hinterlegt.'
            : 'This booking already has a fixed price.'}
        </p>
      )}

      {confirmationIsStale && !isBlock && (
        <div className="rounded-2xl p-3 bg-info-soft text-info text-sm">
          <div className="font-medium mb-1">
            {de ? 'Der Gast hat andere Daten' : 'The guest has different dates'}
          </div>
          <p className="text-xs">
            {de
              ? 'Nach der Änderung stimmt die bereits versendete Bestätigung nicht mehr. Der Gast wird zur erneuten Bestätigung vorgemerkt.'
              : 'Once saved, the confirmation already sent no longer matches. The guest will be flagged for a fresh confirmation.'}
          </p>
        </div>
      )}

      {!isBlock && newPriced && newPriced.warnings.length > 0 && (
        <ul className="text-xs text-warn space-y-1">
          {newPriced.warnings.map((w, i) => (
            <li key={i}>
              {w.code === 'min_nights' ? t('booking.minNights', { n: w.nights })
                : w.code === 'max_occupancy' ? t('booking.tooManyGuests', { max: w.max })
                : w.code === 'unknown_child_ages' ? t('touristTax.unknownAges')
                : ''}
            </li>
          ))}
        </ul>
      )}

      {change.arrival < todayISO() && (
        <Badge tone="warn">{de ? 'Liegt in der Vergangenheit' : 'In the past'}</Badge>
      )}
    </Sheet>
  )
}

function Row({ label, from, to }: { label: string; from: string; to: string }) {
  const changed = from !== to
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted min-w-[6.5rem]">{label}</span>
      <span className={cx(changed && 'line-through opacity-60')}>{from}</span>
      {changed && (
        <>
          <ArrowRight size={14} className="text-muted" />
          <span className="font-medium">{to}</span>
        </>
      )}
    </div>
  )
}
